'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import Link from 'next/link'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  FileCheck2,
  FileText,
  HelpCircle,
  Mail,
  Phone,
  Search,
  ShieldCheck,
  UserCheck,
} from 'lucide-react'
import MediaUploadMulti, { type UploadedAsset } from '../../../components/MediaUploadMulti'
import { useRequireAuth } from '../../../components/auth/AuthGate'
import { OnboardingNudge } from '../../../components/onboarding'
import {
  AIAlert,
  AIRecommendation,
  Badge,
  Button,
  ButtonLink,
  Card,
  MetricPill,
  StatusBadge,
} from '../../../components/ui'
import type { MasjidClaimRole } from '../../../lib/masjid-claims-store'
import { recordPilotClientConversion } from '../../../lib/pilot-client'

type ClaimStep = 'search' | 'verify' | 'docs' | 'review'

type ClaimableMasjid = {
  id: string
  slug: string
  name: string
  city: string
  area: string
  verified: boolean
}

const PAGE_SIZE = 20

const steps: Array<{ id: ClaimStep; title: string }> = [
  { id: 'search', title: 'Find masjid' },
  { id: 'verify', title: 'Representative details' },
  { id: 'docs', title: 'Proof note' },
  { id: 'review', title: 'Review' },
]

const roleOptions: Array<{ value: MasjidClaimRole; label: string }> = [
  { value: 'imam', label: 'Imam' },
  { value: 'committee_member', label: 'Committee member' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'other', label: 'Other representative' },
]

const initialForm = {
  claimantName: '',
  role: '' as '' | MasjidClaimRole,
  mobile: '',
  email: '',
  relationshipNote: '',
  documentReference: '',
  proofNote: '',
  consent: false,
}

export default function MasjidClaimPage() {
  const requireAuth = useRequireAuth()
  const [step, setStep] = useState<ClaimStep>('search')
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<ClaimableMasjid[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<ClaimableMasjid | null>(null)
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [claimId, setClaimId] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const slug = params.get('masjidSlug') || params.get('slug')
    const name = params.get('name')
    if (!slug && !name) return

    const next: ClaimableMasjid = {
      id: params.get('masjidId') || slug || 'unknown',
      slug: slug || 'unknown-masjid',
      name: name || 'Selected masjid',
      city: params.get('city') || 'Hyderabad',
      area: params.get('locality') || params.get('area') || 'Local community',
      verified: false,
    }
    setSelected(next)
    setSearch(next.name)
    setErrors({})
    recordPilotClientConversion({
      eventType: 'claim_started',
      masjidSlug: next.slug,
      path: '/masjid/claim',
    }, { oncePerSession: true })
  }, [])

  const stepIndex = steps.findIndex((item) => item.id === step)
  const progress = (stepIndex / (steps.length - 1)) * 100

  // reset to the first page whenever the query changes
  useEffect(() => { setPage(0) }, [search])

  /* Search-first typeahead over the REAL masjid directory (name / locality /
     city), debounced, 20 per page. Nothing renders until the user types — the
     step stays compact (≤2 viewports) before searching. */
  const query = search.trim()
  useEffect(() => {
    if (query.length < 2) { setResults([]); setHasMore(false); setSearching(false); return }
    let alive = true
    setSearching(true)
    const t = setTimeout(() => {
      fetch(`/api/masjids?q=${encodeURIComponent(query)}&paginate=1&limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!alive) return
          const rows: ClaimableMasjid[] = (d?.masjids || []).map((m: any) => ({
            id: String(m.id), slug: String(m.slug), name: String(m.nameEnglish || 'Masjid'),
            city: String(m.city || ''), area: String(m.district || m.addressLine1 || ''),
            verified: Boolean(m.verified),
          }))
          setResults(rows)
          setHasMore(Boolean(d?.hasMore))
        })
        .catch(() => { if (alive) { setResults([]); setHasMore(false) } })
        .finally(() => { if (alive) setSearching(false) })
    }, 280)
    return () => { alive = false; clearTimeout(t) }
  }, [query, page])

  function setField(key: keyof typeof initialForm, value: string | boolean) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  function validateRole() {
    const next: Record<string, string> = {}
    if (!form.claimantName.trim()) next.claimantName = 'Your full name is required.'
    if (!form.role) next.role = 'Select your masjid relationship.'
    if (!form.mobile.trim()) next.mobile = 'Mobile number is required for verification.'
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid email address.'
    if (form.relationshipNote.trim().length < 10) next.relationshipNote = 'Explain your relationship to this masjid.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  function validateDocs() {
    const next: Record<string, string> = {}
    if (form.proofNote.trim().length < 8) next.proofNote = 'Add a proof note or document reference.'
    if (!form.consent) next.consent = 'Consent is required before submitting.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  function next() {
    if (step === 'search') {
      if (!selected) {
        setErrors({ search: 'Select a masjid to continue.' })
        return
      }
      setStep('verify')
      return
    }
    if (step === 'verify') {
      if (validateRole()) setStep('docs')
      return
    }
    if (step === 'docs') {
      if (validateDocs()) setStep('review')
    }
  }

  function back() {
    const previous = steps[Math.max(stepIndex - 1, 0)]
    setStep(previous.id)
  }

  async function submit() {
    if (!selected || !validateRole() || !validateDocs()) return
    setSubmitting(true)
    setErrors({})
    try {
      const res = await fetch('/api/masjid-claims', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masjidId: selected.id,
          masjidSlug: selected.slug,
          masjidName: selected.name,
          locality: selected.area,
          city: selected.city,
          claimantName: form.claimantName,
          mobile: form.mobile,
          email: form.email,
          role: form.role,
          relationshipNote: form.relationshipNote,
          documentReference: form.documentReference,
          proofNote: form.proofNote,
          consent: form.consent,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrors(data.errors || { submit: data.error || 'Claim submission failed.' })
        return
      }
      setClaimId(data.claim?.id || '')
      setSubmitted(true)
    } catch {
      setErrors({ submit: 'Could not reach the claim service. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  // Multi-file proof: each upload already persists as its own private media_assets row (keyed by the
  // masjid slug), so we don't fold anything into documentReference. We just track how many uploaded to
  // pre-fill the required proofNote for convenience and clear its error.
  function onProofChange(assets: UploadedAsset[]) {
    if (assets.length > 0) {
      setForm((prev) => ({
        ...prev,
        proofNote: prev.proofNote || `${assets.length} proof ${assets.length === 1 ? 'document' : 'documents'} uploaded (${assets.map((a) => a.originalName).join(', ')}).`,
      }))
      setErrors((prev) => ({ ...prev, proofNote: '' }))
    }
  }

  return (
    <main className="entry-page">
      <div className="entry-shell">
        <div className="entry-layout">
          <section className="entry-card">
            <Link href="/masjids" className="entry-kicker"><ArrowLeft size={14} /> Back to masjids</Link>
            <div>
              <h1 className="entry-title">Claim a masjid with trust.</h1>
              <p className="entry-lede">
                Committee access controls public institution data, announcements, prayer times, donations, and MasjidOS workflows. Claims are reviewed before approval.
              </p>
            </div>

            <Card padding="lg" className="entry-card">
              <div className="entry-card-head">
                <div>
                  <span className="entry-kicker"><ShieldCheck size={14} /> Committee verification</span>
                  <h2>{submitted ? 'Claim submitted' : steps[stepIndex].title}</h2>
                </div>
                <StatusBadge tone={submitted ? 'emerald' : 'info'}>{submitted ? 'Under review' : 'Protected flow'}</StatusBadge>
              </div>

              <div className="entry-progress">
                <div className="entry-step-row">
                  {steps.map((item, index) => (
                    <div key={item.id} className={`entry-step ${step === item.id ? 'is-active' : stepIndex > index || submitted ? 'is-done' : ''}`}>
                      <span className="entry-step-dot">{stepIndex > index || submitted ? <CheckCircle2 size={13} /> : index + 1}</span>
                      <span>{item.title}</span>
                    </div>
                  ))}
                </div>
                <div className="entry-progress-track"><div className="entry-progress-fill" style={{ '--entry-progress': `${submitted ? 100 : progress}%` } as CSSProperties} /></div>
              </div>

              {submitted ? (
                <div className="entry-empty">
                  <CheckCircle2 size={42} />
                  <h2>Your claim is under review</h2>
                  <p className="entry-muted">
                    We will verify your role, proof note, and masjid identity before enabling dashboard access. You will receive updates by phone or email.
                  </p>
                  {claimId && <p className="entry-muted">Reference ID: <strong>{claimId}</strong></p>}
                  <div className="entry-actions">
                    <ButtonLink href={`/masjids/${selected?.slug || ''}`} rightIcon={<ArrowRight size={14} />}>View masjid profile</ButtonLink>
                    <ButtonLink href="/dashboard/committee" variant="secondary">Preview committee dashboard</ButtonLink>
                  </div>
                </div>
              ) : (
                <>
                  {step === 'search' && (
                    <div className="entry-form">
                      <label className="entry-field">
                        <span>Search existing masjid</span>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, city, or area" autoFocus />
                      </label>
                      {selected && (
                        <AIAlert
                          title={`Ready to claim ${selected.name}`}
                          body={`${selected.area}, ${selected.city}. This claim will be privately reviewed before any dashboard access is granted.`}
                          tone="info"
                        />
                      )}
                      {errors.search && <div className="entry-error"><Search size={16} /> <span>{errors.search}</span></div>}

                      {query.length < 2 ? (
                        <p className="entry-muted entry-search-hint">
                          <Search size={15} /> Start typing your masjid&rsquo;s name, locality or city to find it.
                        </p>
                      ) : searching && results.length === 0 ? (
                        <p className="entry-muted entry-search-hint">Searching…</p>
                      ) : results.length === 0 ? (
                        <div className="entry-empty">
                          <Search size={32} />
                          <h3>No masjid matches &ldquo;{query}&rdquo;</h3>
                          <p className="entry-muted">If the masjid is not listed yet, register it through guided onboarding.</p>
                          <ButtonLink href="/onboard" rightIcon={<ArrowRight size={14} />}>Register masjid</ButtonLink>
                        </div>
                      ) : (
                        <>
                          <div className="entry-list">
                            {results.map((masjid) => (
                              <button
                                key={masjid.id}
                                type="button"
                                className={`entry-list-item ${selected?.slug === masjid.slug ? 'is-active' : ''}`}
                                onClick={() => {
                                  setSelected(masjid)
                                  setErrors({})
                                  recordPilotClientConversion({
                                    eventType: 'claim_started',
                                    masjidSlug: masjid.slug,
                                    path: '/masjid/claim',
                                  }, { oncePerSession: true })
                                }}
                              >
                                <span className="entry-list-main">
                                  <span className="entry-icon-box"><Building2 size={18} /></span>
                                  <span>
                                    <strong>{masjid.name}</strong>
                                    <p className="entry-muted">{[masjid.area, masjid.city].filter(Boolean).join(', ')}</p>
                                  </span>
                                </span>
                                <span>
                                  {selected?.slug === masjid.slug
                                    ? <StatusBadge tone="emerald">Selected</StatusBadge>
                                    : masjid.verified ? <StatusBadge tone="info">Verified</StatusBadge> : null}
                                </span>
                              </button>
                            ))}
                          </div>
                          {(page > 0 || hasMore) && (
                            <div className="entry-pager">
                              <Button variant="outline" size="sm" disabled={page === 0 || searching} onClick={() => setPage((p) => Math.max(0, p - 1))} leftIcon={<ArrowLeft size={14} />}>Previous</Button>
                              <span className="entry-muted">Page {page + 1}</span>
                              <Button variant="outline" size="sm" disabled={!hasMore || searching} onClick={() => setPage((p) => p + 1)} rightIcon={<ArrowRight size={14} />}>Next</Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {step === 'verify' && (
                    <div className="entry-form">
                      {selected && <AIAlert title={`Claiming ${selected.name}`} body={`${[selected.area, selected.city].filter(Boolean).join(', ')}. Committee verification is required before dashboard control is enabled.`} tone="info" />}
                      <div className="entry-field-row">
                        <label className="entry-field">
                          <span>Claimant name *</span>
                          <input className={errors.claimantName ? 'is-invalid' : ''} value={form.claimantName} onChange={(e) => setField('claimantName', e.target.value)} placeholder="Mohammed Khan" />
                          {errors.claimantName && <small className="entry-error">{errors.claimantName}</small>}
                        </label>
                        <label className="entry-field">
                          <span>Role *</span>
                          <select className={errors.role ? 'is-invalid' : ''} value={form.role} onChange={(e) => setField('role', e.target.value as MasjidClaimRole)}>
                            <option value="">Select role</option>
                            {roleOptions.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                          </select>
                          {errors.role && <small className="entry-error">{errors.role}</small>}
                        </label>
                      </div>
                      <div className="entry-field-row">
                        <label className="entry-field">
                          <span>Mobile number *</span>
                          <input className={errors.mobile ? 'is-invalid' : ''} value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} placeholder="+91 98765 43210" />
                          {errors.mobile && <small className="entry-error">{errors.mobile}</small>}
                        </label>
                        <label className="entry-field">
                          <span>Email</span>
                          <input className={errors.email ? 'is-invalid' : ''} type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@example.com" />
                          {errors.email && <small className="entry-error">{errors.email}</small>}
                        </label>
                      </div>
                      <label className="entry-field">
                        <span>Masjid relationship note *</span>
                        <textarea className={errors.relationshipNote ? 'is-invalid' : ''} value={form.relationshipNote} onChange={(e) => setField('relationshipNote', e.target.value)} placeholder="Briefly explain your role, committee connection, or authorization to manage this masjid profile." />
                        {errors.relationshipNote && <small className="entry-error">{errors.relationshipNote}</small>}
                      </label>
                    </div>
                  )}

                  {step === 'docs' && (
                    <div className="entry-form">
                      <AIRecommendation
                        title="Proof can be lightweight for MVP"
                        body="Upload a private proof document or add a clear note. Private files are visible only to review/admin workflows."
                      />
                      <MediaUploadMulti
                        label="Private proof documents (up to 6)"
                        ownerType="claim"
                        ownerId={selected?.slug || 'pending-claim'}
                        masjidSlug={selected?.slug}
                        masjidId={selected?.id}
                        purpose="proof"
                        max={6}
                        onChange={onProofChange}
                        helper="Private — visible only to reviewers, never public. Ownership letter, committee resolution, ID, or a photo. Images, PDF, Word, Excel, text. Max 15MB each."
                      />

                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, padding: '14px 16px', background: '#f8fafc' }}>
                        <strong style={{ display: 'block', color: '#12312d', fontSize: 13.5, marginBottom: 8 }}>For verification, please upload:</strong>
                        <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 7, color: '#334155', fontSize: 13, lineHeight: 1.5 }}>
                          <li><strong>Photos of the masjid</strong> — exterior, interior, and the name board / signage</li>
                          <li>
                            <strong>A committee authorization note</strong> — a signed letter (or a photo of a handwritten one) on masjid letterhead authorizing you to manage this masjid{' '}
                            <span style={{ display: 'inline-block', marginLeft: 4, padding: '1px 8px', borderRadius: 999, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: 11, fontWeight: 900, letterSpacing: '.03em' }}>⚠️ REQUIRED</span>
                          </li>
                          <li><strong>Your contact details and role</strong> in the committee</li>
                        </ul>
                        <p style={{ margin: '10px 0 0', color: '#b91c1c', fontSize: 12.5, fontWeight: 800 }}>The committee authorization note is compulsory for verification.</p>
                      </div>
                      <label className="entry-field">
                        <span>Document link or reference</span>
                        <input value={form.documentReference} onChange={(e) => setField('documentReference', e.target.value)} placeholder="Secure Drive link, committee letter number, or registration reference" />
                      </label>
                      <label className="entry-field">
                        <span>Proof note *</span>
                        <textarea className={errors.proofNote ? 'is-invalid' : ''} value={form.proofNote} onChange={(e) => setField('proofNote', e.target.value)} placeholder="Describe the document/proof you can provide, who can verify it, and any committee context." />
                        {errors.proofNote && <small className="entry-error">{errors.proofNote}</small>}
                      </label>
                      <label className="entry-field" style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
                        <input type="checkbox" checked={form.consent} onChange={(e) => setField('consent', e.target.checked)} style={{ width: 18, marginTop: 3 }} />
                        <span>I confirm I am authorized to submit this claim and consent to Masjids.in contacting me for verification.</span>
                      </label>
                      {errors.consent && <small className="entry-error">{errors.consent}</small>}
                    </div>
                  )}

                  {step === 'review' && (
                    <div className="entry-card">
                      <div className="entry-card-grid">
                        <MetricPill label="Verification stage" value="Manual" tone="emerald" />
                        <MetricPill label="Public changes" value="Locked" tone="gold" />
                        <MetricPill label="Review estimate" value="2-3 days" tone="info" />
                      </div>
                      <Card tone="muted" padding="md">
                        <h3 className="card-title">{selected?.name}</h3>
                        <p className="entry-muted">{selected?.area}, {selected?.city} - claimed by {form.claimantName || 'representative'} ({roleOptions.find((item) => item.value === form.role)?.label || 'role pending'})</p>
                      </Card>
                      <div className="entry-note">
                        <HelpCircle size={16} />
                        <span>Submitting does not immediately change the public profile. Moderators review the claim and may request more information before approval.</span>
                      </div>
                      {errors.submit && <div className="entry-error">{errors.submit}</div>}
                    </div>
                  )}

                  <div className="entry-actions">
                    {step !== 'search' && <Button variant="outline" onClick={back} leftIcon={<ArrowLeft size={15} />}>Back</Button>}
                    {step === 'review' ? (
                      <Button onClick={() => requireAuth({ onAuthed: submit, label: 'Create a free account to submit your claim.' })} disabled={submitting} rightIcon={<CheckCircle2 size={15} />}>{submitting ? 'Submitting...' : 'Submit claim'}</Button>
                    ) : (
                      <Button onClick={next} rightIcon={<ArrowRight size={15} />}>Continue</Button>
                    )}
                  </div>
                </>
              )}
            </Card>
          </section>

          <aside className="entry-help-rail">
            <OnboardingNudge
              eyebrow="Verification promise"
              title="Masjid control is protected."
              body="Claim requests are reviewed because dashboard access can affect public prayer data, announcements, donations, and committee operations."
              steps={[
                { title: 'Identity. ', body: 'We confirm your role and reachable contact details.' },
                { title: 'Proof. ', body: 'Your relationship note and document reference are reviewed privately.' },
                { title: 'Approval. ', body: 'Access is enabled only after a trusted review.' },
              ]}
              actions={[
                { href: '/onboard', label: 'Register new masjid', variant: 'secondary' },
                { href: '/masjids', label: 'Open masjids', variant: 'outline' },
              ]}
              tone="gold"
            />
            <Card padding="md">
              <div className="entry-card-head">
                <div>
                  <Badge tone="neutral">Private review</Badge>
                  <h3 className="card-title">Prepare before submitting</h3>
                </div>
                <FileCheck2 size={18} />
              </div>
              <div className="entry-list">
                {[
                  { icon: UserCheck, title: 'Role proof', body: 'Committee resolution, imam authorization, trustee note, or equivalent context.' },
                  { icon: FileText, title: 'Institution proof', body: 'Registration, waqf, trust, society, or committee document reference.' },
                  { icon: Phone, title: 'Reachable contact', body: 'Mobile is required for verification follow-up.' },
                  { icon: Mail, title: 'Email optional', body: 'Email helps with status updates and document requests.' },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <div key={item.title} className="entry-list-main">
                      <span className="entry-icon-box"><Icon size={17} /></span>
                      <div>
                        <strong>{item.title}</strong>
                        <p className="entry-muted">{item.body}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  )
}
